// React hook for the RP-2b-ii Settings UI: exposes the current
// capability overrides plus an "is effective?" probe and a setter
// for the per-capability override.
//
// Distinct from `useUserCapabilities` (which returns a bare
// `HasCapabilityFn` for the dock gate evaluator) so the Settings UI
// can introspect the *override* state (explicit on / explicit off /
// no preference) without paying the predicate's "did any of these
// resolve true?" abstraction tax. Both hooks subscribe to the same
// storage primitive in `capabilityStorage.ts` so any write from one
// surface (the Settings toggle, a future remote sync, etc.)
// propagates everywhere immediately.
//
// `isEffective` mirrors the resolution policy from
// `capabilityResolution.ts` (explicit override > role-derived
// default) so the toggle's visible "checked" state is byte-for-byte
// the same value any consumer (gate evaluator, daily-checklist
// filter, achievement gallery) would compute. The override map is
// surfaced separately so future UI work can distinguish "user has
// taken a deliberate stance" from "user is on the role default" —
// e.g. a "reset to default" affordance — without re-deriving from
// localStorage.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import type { Capability } from "@/lib/capabilities/capabilities";
import {
  getCapabilityOverrides,
  setCapabilityOverride as persistCapabilityOverride,
  subscribeCapabilityOverrides,
  type CapabilityOverrides,
} from "@/lib/capabilities/capabilityStorage";
import { isCapabilityEffective } from "@/lib/capabilities/capabilityResolution";

export type UseUserCapabilitiesEditorResult = {
  /** The current stored overrides snapshot. Live; updates on writes
   * (including cross-tab `storage` events). */
  overrides: CapabilityOverrides;
  /** Resolves (override ∪ role-derived default) → effective boolean
   * for a single capability. Stable reference per `(overrides, auth)`
   * snapshot. */
  isEffective: (capability: Capability) => boolean;
  /** Write helper. Pass `null` to clear the override (back to the
   * role-derived default); pass `true` / `false` to record an
   * explicit preference. Synchronously notifies all storage
   * subscribers (including this hook + `useUserCapabilities`). */
  setOverride: (capability: Capability, value: boolean | null) => void;
};

export function useUserCapabilitiesEditor(): UseUserCapabilitiesEditorResult {
  const auth = useAuth();
  const [overrides, setOverrides] = useState<CapabilityOverrides>(
    getCapabilityOverrides,
  );

  useEffect(() => {
    const unsubscribe = subscribeCapabilityOverrides(setOverrides);
    // Re-sync on mount in case storage changed between the static
    // initial-state read and the subscription attaching.
    setOverrides(getCapabilityOverrides());
    return unsubscribe;
  }, []);

  const isEffective = useMemo(
    () =>
      (capability: Capability) =>
        isCapabilityEffective(capability, overrides, (...roles) =>
          auth.hasRole(...roles),
        ),
    [overrides, auth],
  );

  const setOverride = useCallback(
    (capability: Capability, value: boolean | null) => {
      persistCapabilityOverride(capability, value);
      // No setState here — the storage subscriber fires synchronously
      // from inside `persistCapabilityOverride`, which lands the new
      // value on every subscriber including this hook.
    },
    [],
  );

  return { overrides, isEffective, setOverride };
}
